import {
  Button,
  ConfigProvider,
  Drawer,
  Grid,
  Layout as AntdLayout,
  Menu,
  MenuProps,
  Avatar,
  Badge,
  Tooltip,
} from 'antd'

import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { getClient } from 'src/sentry/buffer'

import BarsOutlined from '@ant-design/icons/BarsOutlined'
import BugOutlined from '@ant-design/icons/BugOutlined'
import GiftOutlined from '@ant-design/icons/GiftOutlined'
import LogoutOutlined from '@ant-design/icons/LogoutOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'

import { useIdentity } from '@/hooks/auth/useIdentity'
import { authProvider } from 'src/authProvider'
import { urqlClient } from '@/lib/urqlClient'
import { canAccess } from 'src/accessControl'
import { navigationItems } from '@/config/navigation'
import { useChangelogModal } from '@features/changelog'

import { Title as DefaultTitle } from '../title'
import { drawerButtonStyles } from './styles'
import { useAccountProfile } from '@/hooks/useAccountProfile'
import { isProjectHubRole } from 'src/utils/projectHubRole'
import { useCreationRights } from '@hooks/useCreationRights'
import { useHubNavPresence } from '@hooks/useHubNavPresence'
import { AccountType } from 'src/utils/enums'
import { Account } from 'types'
import { useSiderContext } from './context'

export const Sider: React.FC = () => {
  const { collapsed, setCollapsed } = useSiderContext()
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [isInitialMount, setIsInitialMount] = useState<boolean>(true)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const breakpoint = Grid.useBreakpoint()
  const selectedKey = '/' + (pathname?.split('/')?.[1] || '')

  const { data: identity } = useIdentity<Account>()
  const accountType = identity?.account_type
  const { hasUpdates: changelogHasUpdates } = useChangelogModal()

  // Shared with the projects-hub greeting — one cached `account-header-info`
  // query resolves the logged-in account's name from its account_type relation.
  const { profile, fullName } = useAccountProfile()

  const [allowedKeys, setAllowedKeys] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!accountType) {
      setAllowedKeys(new Set())
      return
    }
    let cancelled = false
    Promise.all(
      navigationItems.map(async (item) => {
        const can = await canAccess(accountType, 'list', item.key)
        return [item.key, can] as const
      })
    ).then((rows) => {
      if (cancelled) return
      setAllowedKeys(
        new Set(rows.filter(([, allowed]) => allowed).map(([key]) => key))
      )
    })
    return () => {
      cancelled = true
    }
  }, [accountType])

  const handleLogout = async () => {
    const result = await authProvider(urqlClient).logout({})
    navigate(result.redirectTo || '/login', { replace: true })
  }

  const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg

  const [feedbackAvailable, setFeedbackAvailable] = useState(false)

  const getFeedbackIntegration = () =>
    getClient()?.getIntegrationByName?.('Feedback') as
      | { openDialog: () => void }
      | undefined

  useEffect(() => {
    setFeedbackAvailable(Boolean(getFeedbackIntegration()))
  }, [])

  const openBugReport = () => {
    getFeedbackIntegration()?.openDialog()
  }

  const renderUserSection = () => {
    if (!profile) return null

    return (
      <div
        style={{
          padding: '16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: '#548052',
          marginTop: 'auto',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {feedbackAvailable && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Tooltip title="Signaler un bug" placement="right">
              <BugOutlined
                style={{
                  fontSize: collapsed ? '20px' : '24px',
                  color: '#fff',
                  cursor: 'pointer',
                }}
                onClick={openBugReport}
              />
            </Tooltip>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: collapsed ? 'column' : 'row',
            gap: collapsed ? '8px' : '16px',
            width: '100%',
          }}
        >
          <Tooltip title="Nouveautés" placement="right">
            <Link
              to="/changelog"
              aria-label="Nouveautés"
              onClick={() => setDrawerOpen(false)}
              style={{ display: 'inline-flex', cursor: 'pointer' }}
            >
              <Badge dot={changelogHasUpdates} offset={[-2, 2]}>
                <GiftOutlined
                  style={{
                    fontSize: collapsed ? '20px' : '24px',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                />
              </Badge>
            </Link>
          </Tooltip>
          <Tooltip
            title={`${fullName || 'Mon compte'} — Mon compte`}
            placement="right"
          >
            <Link
              to="/account"
              aria-label="Mon compte"
              onClick={() => setDrawerOpen(false)}
              style={{ display: 'inline-flex', cursor: 'pointer' }}
            >
              <Avatar
                size={collapsed ? 'default' : 'large'}
                src={`https://xsgames.co/randomusers/avatar.php?g=pixel`}
                style={{ cursor: 'pointer' }}
              />
            </Link>
          </Tooltip>
          <Tooltip title="Déconnexion" placement="right">
            <LogoutOutlined
              style={{
                fontSize: collapsed ? '20px' : '24px',
                color: '#ef4444',
                cursor: 'pointer',
              }}
              onClick={handleLogout}
            />
          </Tooltip>
        </div>
      </div>
    )
  }

  // Project-hub roles (SALESMAN + SOCIETY) share the simplified hub sidebar.
  const isSalesman = isProjectHubRole(accountType)
  // The company (SOCIETY) sees the same hub but oversees the whole company:
  // its projects list spans every salesman, and it manages the salesman roster
  // (#379), so it also gets the "Commerciaux" entry.
  const isCompany = accountType === AccountType.SOCIETY
  // Both hub roles can start a new request: a salesman for itself, a company
  // on behalf of one of its salesmen (picked during creation) — but only for
  // the kinds their company is authorised to create (#655).
  //
  // The CTA opens the CHOOSER, not a wizard, so it survives while EITHER right
  // stands and disappears only when both are off. Gating it on the project
  // right alone would hide the studies path from a study-only company, which is
  // the exact case this feature exists to serve.
  const { canCreateProject, canCreateUrbanAnalysis } = useCreationRights()
  const canStartRequest =
    isProjectHubRole(accountType) &&
    (canCreateProject || canCreateUrbanAnalysis)

  // Hub menu — labels + order per hub role. The SALESMAN mock (#321) is
  // first-person ("Mes projets"); the company oversees all its salesmen so it
  // reads plainly ("Projets") and gains "Commerciaux" for roster management.
  const HUB_LABEL: Record<string, string> = isCompany
    ? {
        projects: 'Projets',
        urban_analysis: 'Études urbaines',
        clients: 'Clients',
        salesmens: 'Commerciaux',
      }
    : {
        projects: 'Mes projets',
        urban_analysis: 'Études urbaines',
        clients: 'Mes clients',
      }
  // An entry the commercial can neither use nor fill is a dead end: it leads to
  // an empty list with no way to put anything in it. So each of the two work
  // areas is offered while EITHER it holds something OR the company may create
  // that kind of request — a company restricted to urban studies keeps
  // "Études urbaines" and loses "Projets" only once it owns none.
  //
  // "Clients" and "Commerciaux" are not gated: both are reachable-and-fillable
  // in their own right, and the roster is how a company adds the salesmen that
  // would own the work in the first place.
  const { hasProjects, hasUrbanAnalyses } = useHubNavPresence()
  const hubAreaOffered: Record<string, boolean> = {
    projects: hasProjects || canCreateProject,
    urban_analysis: hasUrbanAnalyses || canCreateUrbanAnalysis,
  }

  const HUB_ORDER: ReadonlyArray<string> = (
    isCompany
      ? ['projects', 'urban_analysis', 'clients', 'salesmens']
      : ['projects', 'urban_analysis', 'clients']
  ).filter((key) => hubAreaOffered[key] ?? true)

  const items: MenuProps['items'] = isSalesman
    ? [
        ...HUB_ORDER.map((k) => navigationItems.find((i) => i.key === k))
          .filter(
            (i): i is (typeof navigationItems)[number] =>
              !!i && allowedKeys.has(i.key)
          )
          .map((item) => ({
            key: item.path,
            style: {
              fontWeight: item.path === selectedKey ? 'bold' : 'normal',
              paddingTop: '6px',
            },
            icon: item.icon,
            label: (
              <Link to={item.path} data-cy={`nav--${item.key}`}>
                {HUB_LABEL[item.key]}
              </Link>
            ),
          })),
      ]
    : navigationItems
        .filter((item) => allowedKeys.has(item.key))
        .map((item) => ({
          key: item.path,
          style: {
            fontWeight: item.path === selectedKey ? 'bold' : 'normal',
            paddingTop: '6px',
          },
          icon: item.icon,
          label: (
            <Link to={item.path} data-cy={`nav--${item.key}`}>
              {item.label}
            </Link>
          ),
        }))

  const renderSalesmanCta = () => {
    if (!canStartRequest) return null
    return (
      <div
        style={{
          padding: collapsed ? '12px 8px' : '12px 16px',
          width: '100%',
        }}
      >
        <Tooltip title={collapsed ? 'Nouvelle demande' : ''} placement="right">
          <Link
            to="/new-request"
            data-cy="action--new-request"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              padding: collapsed ? '10px 0' : '10px 12px',
              borderRadius: 10,
              background: '#f97316',
              color: 'white',
              fontWeight: 600,
              fontSize: 14,
              textDecoration: 'none',
              boxShadow: '0 4px 14px rgba(249, 115, 22, 0.35)',
            }}
          >
            <PlusOutlined style={{ fontSize: 16 }} />
            {!collapsed && <span>Nouvelle demande</span>}
          </Link>
        </Tooltip>
      </div>
    )
  }

  const renderMenu = () => {
    return (
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Menu
          selectedKeys={[selectedKey]}
          mode="inline"
          onClick={() => {
            setDrawerOpen(false)
            if (!breakpoint.lg) {
              setCollapsed(true)
            }
          }}
          items={items}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: 'transparent',
          }}
          inlineCollapsed={collapsed}
        />
      </div>
    )
  }

  const renderDrawerSider = () => {
    return (
      <>
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          closable={false}
          width={200}
          style={{
            padding: 0,
          }}
          maskClosable={true}
        >
          <AntdLayout>
            <AntdLayout.Sider
              style={{
                height: '100vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                left: 0,
                top: 0,
                bottom: 0,
                zIndex: 1000,
              }}
            >
              <DefaultTitle collapsed={false} />
              {renderSalesmanCta()}
              {renderMenu()}
              {renderUserSection()}
            </AntdLayout.Sider>
          </AntdLayout>
        </Drawer>
        <Button
          style={drawerButtonStyles}
          size="large"
          onClick={() => setDrawerOpen(true)}
          icon={<BarsOutlined />}
        ></Button>
      </>
    )
  }

  const renderContent = () => {
    if (isMobile) {
      return renderDrawerSider()
    }

    return (
      <AntdLayout.Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(newCollapsed: boolean): void => {
          if (isInitialMount) {
            setIsInitialMount(false)
            return
          }

          setCollapsed(newCollapsed)
          if (!newCollapsed) {
            setTimeout(() => setCollapsed(true), 10_000)
          }
        }}
        collapsedWidth={80}
        breakpoint="lg"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1000,
          overflow: 'hidden',
        }}
        width={200}
      >
        <DefaultTitle collapsed={collapsed} />
        {renderSalesmanCta()}
        {renderMenu()}
        {renderUserSection()}
      </AntdLayout.Sider>
    )
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            itemBg: 'transparent',
            itemColor: '#fff',
            itemSelectedColor: '#fff',
            itemSelectedBg: '#64A961',
            itemHoverColor: '#fff',
            itemHoverBg: '#64A961',
            itemMarginInline: 0,
            itemPaddingInline: 16,
          },
          Layout: {
            headerBg: '#548052',
            siderBg: '#548052',
            triggerBg: '#679C64',
          },
        },
      }}
    >
      {renderContent()}
    </ConfigProvider>
  )
}
